/** Compact V1/V2 intrusion status and command card. */
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, Card, IconButton, List, Stack, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import IntrusionCommandsDialog from '@sophocles/views/sites/components/IntrusionSensorCard/IntrusionCommandsDialog';
import IntrusionCommandsDialogV2 from '@sophocles/views/sites/components/IntrusionSensorCard/IntrusionCommandsDialogV2';
import ScheduleListItem from '@sophocles/views/sites/components/RelayCards/ScheduleListItem';
import DashboardCardSkeleton from '@sophocles/views/sites/components/SiteDashboard/DashboardCardSkeleton';

function EmptyState({ children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, py: 2, textAlign: 'center' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {children}
      </Typography>
    </Box>
  );
}

function IntrusionListItem({ number, name, disabled = false, enabled = true, onClick }) {
  const { t } = useTranslation();
  const displayName = name || t('general.unnamed', 'Unnamed');

  return (
    <ScheduleListItem
      disabled={disabled}
      onClick={onClick}
      primaryText={null}
      secondaryText={
        <span
          className="schedule-list-secondary-text"
          style={{
            display: 'flex',
            flexDirection: 'column',
            opacity: enabled ? 1 : 0.6,
          }}
        >
          <span>
            <span style={{ fontWeight: 'bold' }}>{number}. </span>
            {enabled ? displayName : t('general.disabled', 'Disabled')}
          </span>
          {!enabled && name ? <span>{displayName}</span> : null}
        </span>
      }
    />
  );
}

/**
 * @param {object} props
 * @param {object} props.site site entity passed to command dialogs
 * @param {'v1'|'v2'} props.variant intrusion hardware generation
 * @param {object|null} props.intrusion unit intrusion summary
 * @param {boolean} [props.loading=false]
 * @param {boolean} [props.canSendCommands=false]
 * @param {boolean} [props.commandLocked=false]
 * @param {boolean} [props.canManageConfiguration=false]
 * @param {Function} [props.onOpenSettings]
 */
export default function IntrusionSummaryCard({
  site,
  variant,
  intrusion = null,
  loading = false,
  canSendCommands = false,
  commandLocked = false,
  canManageConfiguration = false,
  onOpenSettings,
}) {
  const { t } = useTranslation();
  const isV2 = variant === 'v2';

  const [commandsOpen, setCommandsOpen] = useState(false);

  // v1: inputs array of { input_number, name }
  const v1Inputs = !isV2 ? (intrusion?.inputs ?? []).slice().sort((a, b) => a.input_number - b.input_number) : [];
  // v2: sensors array of { id, sensor_number, enabled, name, ... }
  const v2Sensors = isV2 ? (intrusion?.sensors ?? []).slice().sort((a, b) => a.sensor_number - b.sensor_number) : [];

  const sensorCount = isV2 ? v2Sensors.length : v1Inputs.length;
  // Same "no sensors reported" condition the card body uses to decide
  // between its sensor list and the EmptyState below — commands can't
  // target a sensor the unit hasn't reported, so the lock control agrees
  // with the body instead of offering a dialog with nothing to command.
  const hasSensors = sensorCount > 0;
  const commandsTooltip = commandLocked
    ? t('commandActivity.commandsLocked', 'Wait for the queued command to finish before sending another.')
    : hasSensors
      ? t('sites.intrusionCommands', 'Intrusion Commands')
      : t(
          'sites.intrusionSensorConfigMessage',
          'Unit must report that it has an intrusion sensor before this can be configured.',
        );

  if (loading) {
    return <DashboardCardSkeleton variant="intrusion" />;
  }

  return (
    <Card sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.5, flexShrink: 0 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 'var(--card-icon-radius)',
            bgcolor: 'action.hover',
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <SecurityOutlinedIcon fontSize="small" />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
            {t('sites.intrusionSensors', 'Intrusion Sensors')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
            {isV2 ? t('sites.smartLink2', 'SmartLink 2') : t('sites.legacyIntrusion', 'Legacy')}
            {sensorCount > 0 ? ` • ${sensorCount}` : ''}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Tooltip title={commandsTooltip}>
            <span>
              <IconButton
                size="small"
                disabled={!canSendCommands || !hasSensors || commandLocked}
                onClick={() => setCommandsOpen(true)}
                aria-label={t('sites.intrusionCommands', 'Intrusion Commands')}
              >
                <LockOpenOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('sites.intrusionSettings', 'Intrusion Settings')}>
            <span>
              <IconButton
                size="small"
                disabled={!canManageConfiguration || !onOpenSettings}
                onClick={() => onOpenSettings?.()}
                aria-label={t('sites.intrusionSettings', 'Intrusion Settings')}
              >
                <SettingsOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
        {isV2 ? (
          v2Sensors.length > 0 ? (
            <List disablePadding>
              {v2Sensors.map(sensor => (
                <IntrusionListItem
                  key={sensor.sensor_number}
                  number={sensor.sensor_number}
                  name={sensor.name}
                  enabled={sensor.enabled}
                  disabled={!canManageConfiguration || !onOpenSettings}
                  onClick={() => onOpenSettings?.()}
                />
              ))}
            </List>
          ) : (
            <EmptyState>
              {t('sites.intrusionSensorConfigMessage', 'Intrusion sensor configuration required.')}
            </EmptyState>
          )
        ) : v1Inputs.length === 0 ? (
          <EmptyState>{t('sites.intrusionSensorConfigMessage', 'Intrusion sensor configuration required.')}</EmptyState>
        ) : (
          <List disablePadding>
            {v1Inputs.map(inputName => (
              <IntrusionListItem
                key={inputName.input_number}
                number={inputName.input_number}
                name={inputName.name ?? ''}
                disabled={!canManageConfiguration || !onOpenSettings}
                onClick={() => onOpenSettings?.()}
              />
            ))}
          </List>
        )}
      </Box>

      {commandsOpen &&
        (isV2 ? (
          <IntrusionCommandsDialogV2 site={site} open={commandsOpen} closeDialog={() => setCommandsOpen(false)} />
        ) : (
          <IntrusionCommandsDialog site={site} open={commandsOpen} closeDialog={() => setCommandsOpen(false)} />
        ))}
    </Card>
  );
}

IntrusionSummaryCard.propTypes = {
  /** Immutable site Map — passed through to legacy dialogs unchanged. */
  site: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(['v1', 'v2']).isRequired,
  intrusion: PropTypes.object,
  loading: PropTypes.bool,
  canSendCommands: PropTypes.bool,
  commandLocked: PropTypes.bool,
  canManageConfiguration: PropTypes.bool,
  onOpenSettings: PropTypes.func,
};

EmptyState.propTypes = {
  children: PropTypes.node,
};

IntrusionListItem.propTypes = {
  number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  enabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};
